/**
 * Enhanced LionRapid Core with event-based language change and smart caching
 * filepath: /home/janrau/tm-integration/packages/core/src/lionRapid.ts
 */
import { EventEmitter } from './base/event-emitter.js';
import { HandlerChain } from './handlers/handler-chain.js';
import type {
  FormatterFunction,
  IEmbeddedPlugin,
  IPlugin,
  ValueProcessor,
} from './interfaces/plugin.interface.js';
import type { TranslationKeys } from './types/generated.types.js';
import type { TranslationRequest } from './types/translation.types.js';
import { logger, resolveNestedKey } from './utils/index.js';
import { prepareTranslationRequest } from './utils/translation-flow.js';
import { createNamespaceCacheKey } from './utils/translation-value-utils.js';
import { describeContent, describeError } from './utils/log-safe.js';

// Content module imports for ContentUnit processing
import type { ContentUnit } from './content/types.js';
import { MarkupParser } from './content/parser/MarkupParser.js';
import { HTMLSerializer } from './content/serializer/HTMLSerializer.js';

// Cache module
import { CacheManager } from './cache/CacheManager.js';

/**
 * Regex to detect indexed tags in a string (from server ContentUnit)
 * Matches patterns like <1>, </1>, <2>, </2>, etc.
 */
const INDEXED_TAG_REGEX = /<\/?[0-9]+\/?>/;

/**
 * Regex to detect HTML tags in a string
 * Matches patterns like <b>, <a href="...">, <em>, etc.
 */
const HTML_TAG_REGEX = /<[a-z][a-z0-9]*(?:\s+[^>]*)?\/?>/i;

export class LionRapidCore {
  private handlerChain: HandlerChain;
  private currentLocale: string;
  private defaultNamespace?: string;
  private pendingTranslations = new Map<string, Promise<void>>();
  bus: EventEmitter;

  private plugins = new Map<string, IPlugin>();

  // 🔥 EMBEDDED: Core integration maps
  private formatters = new Map<string, FormatterFunction>();
  private processors = new Map<string, ValueProcessor>();
  private warnedNoInterpolate = false;

  // 📡 EVENT-DRIVEN: Just track for cleanup
  private eventPlugins = new Set<IPlugin>();

  // 📦 CONTENT: Parser and serializer for ContentUnit processing
  private readonly markupParser = new MarkupParser();
  private readonly htmlSerializer = new HTMLSerializer();

  // 🔄 CACHE: TanStack Query-inspired cache management
  public readonly cache: CacheManager;

  constructor(
    eventEmitter: EventEmitter,
    handlerChain: HandlerChain,
    defaultLocale = 'en',
    defaultNamespace?: string
  ) {
    this.bus = eventEmitter;
    this.handlerChain = handlerChain;
    this.currentLocale = defaultLocale;
    this.defaultNamespace = defaultNamespace;

    // Initialize cache manager with dependencies
    this.cache = new CacheManager(
      this.handlerChain,
      this.bus,
      () => this.currentLocale,
      (locale, namespaces) => this.loadNamespaces(locale, namespaces)
    );
  }

  /**
   * ✅ Register plugin (called by builder)
   */
  registerPlugin(plugin: IPlugin): void {
    this.plugins.set(plugin.name, plugin);

    // 🔥 EMBEDDED PLUGINS: Extract functionality for core integration
    if (this.isEmbeddedPlugin(plugin)) {
      this.integrateEmbeddedPlugin(plugin as IEmbeddedPlugin);
    }

    // 📡 EVENT-DRIVEN PLUGINS: Just track for cleanup
    if (this.isEventPlugin(plugin)) {
      this.eventPlugins.add(plugin);
    }
  }

  /**
   * 🔥 EMBEDDED: Integrate plugin functionality into core
   */
  private integrateEmbeddedPlugin(plugin: IEmbeddedPlugin): void {
    // Extract formatters
    if (plugin.getFormatters) {
      const pluginFormatters = plugin.getFormatters();
      Object.entries(pluginFormatters).forEach(([name, formatter]) => {
        this.formatters.set(name, formatter);
        logger.debug(`Embedded formatter: ${name} from ${plugin.name}`);
      });
    }

    // Extract processors
    if (plugin.getProcessors) {
      const pluginProcessors = plugin.getProcessors();
      Object.entries(pluginProcessors).forEach(([name, processor]) => {
        this.processors.set(name, processor);
        logger.debug(`Embedded processor: ${name} from ${plugin.name}`);
      });
    }
  }

  /**
   * 📦 TRANSLATION API - Returns PLAIN TEXT
   *
   * Strips indexed tags and returns safe plain text output.
   * For HTML output with styles, use tHtml() instead.
   *
   * @param key - Translation key
   * @param params - Optional interpolation parameters
   * @param defaultValue - Optional default value
   * @returns Plain text string (safe, no XSS risk)
   *
   * @example
   * ```typescript
   * t('welcome')                    // → "Hello World"
   * t('greeting', { name: 'John' }) // → "Hello John"
   * t('missing', 'Default text')   // → "Default text" (if key missing)
   * ```
   */
  t<K extends string>(
    key: K,
    ...args: K extends keyof TranslationKeys
      ? TranslationKeys[K] extends Record<string, never>
        ? [defaultValue?: string]
        : [params: TranslationKeys[K], defaultValue?: string]
      : [params?: Record<string, unknown>, defaultValue?: string]
  ): string {
    const [firstArg, secondArg] = args;

    const request = prepareTranslationRequest(
      key,
      this.currentLocale,
      firstArg as string | Record<string, unknown> | undefined,
      secondArg as Record<string, unknown> | string | undefined,
      this.defaultNamespace
    );

    // Emit request event
    logger.debug(`Translation requested: ${request.cleanKey}`);
    const translateBus = this.bus.translation(request);
    translateBus.request();

    // Try memory cache (sync)
    const syncResult = this.handlerChain.handleSync(request);
    if (syncResult && syncResult.status === 200) {
      const result = this.processValue(syncResult.value, request);
      logger.debug(
        `Translation found: ${request.cleanKey} from ${syncResult.source} (${syncResult.duration}ms)`
      );
      translateBus.success(result);
      return result;
    }

    // Cache miss - trigger background load
    this.triggerBackgroundLoad(request);

    // Return fallback immediately
    const fallbackValue = request.parsedDefaultValue || request.cleanKey;
    const fallbackResult = this.processValue(fallbackValue, request);
    logger.debug(`Translation fallback: ${request.cleanKey}`);
    translateBus.fallback(fallbackResult);
    return fallbackResult;
  }

  /**
   * 📦 HTML TRANSLATION API
   * Returns translation as HTML string with styles applied.
   *
   * Use for innerHTML, dangerouslySetInnerHTML, or server-side rendering.
   * For React components, prefer using <Trans> instead.
   *
   * @param key - Translation key
   * @param params - Optional interpolation parameters
   * @param defaultValue - Optional default value (can contain HTML)
   * @returns HTML string with styles applied
   *
   * @example
   * ```typescript
   * // Vanilla JS
   * element.innerHTML = i18n.tHtml('welcome');
   * // → "Hello <b>World</b>"
   *
   * // React (use sparingly)
   * <span dangerouslySetInnerHTML={{ __html: tHtml('welcome') }} />
   * ```
   */
  tHtml<K extends string>(
    key: K,
    ...args: K extends keyof TranslationKeys
      ? TranslationKeys[K] extends Record<string, never>
        ? [defaultValue?: string]
        : [params: TranslationKeys[K], defaultValue?: string]
      : [params?: Record<string, unknown>, defaultValue?: string]
  ): string {
    // Pass arguments directly to prepareTranslationRequest
    // which handles the flexible parameter parsing
    const [firstArg, secondArg] = args;

    const request = prepareTranslationRequest(
      key,
      this.currentLocale,
      firstArg as string | Record<string, unknown> | undefined,
      secondArg as Record<string, unknown> | string | undefined,
      this.defaultNamespace
    );

    // Emit request event
    logger.debug(`HTML translation requested: ${request.cleanKey}`);
    const translateBus = this.bus.translation(request);
    translateBus.request();

    // Try memory cache (sync) - result now includes styles
    const syncResult = this.handlerChain.handleSync(request);
    if (syncResult && syncResult.status === 200) {
      const result = this.processValueAsHtml(syncResult.value, request, syncResult.styles);
      logger.debug(
        `HTML translation found: ${request.cleanKey} from ${syncResult.source}`
      );
      translateBus.success(result);
      return result;
    }

    // Cache miss - trigger background load
    this.triggerBackgroundLoad(request);

    // Return fallback immediately (processed as HTML, no styles)
    const fallbackValue = request.parsedDefaultValue || request.cleanKey;
    const fallbackResult = this.processValueAsHtml(fallbackValue, request);
    logger.debug(`HTML translation fallback: ${request.cleanKey}`);
    translateBus.fallback(fallbackResult);
    return fallbackResult;
  }

  /**
   * 📦 Process value as HTML (for tHtml)
   *
   * Converts indexed tags to HTML using styles from result or namespace cache.
   *
   * @internal
   */
  private processValueAsHtml(
    value: string,
    request: TranslationRequest,
    resultStyles?: Record<string, string[]>
  ): string {
    // Process ICU first
    const processedText = this.processTranslationValue(value, request);

    // Check for indexed tags (from server ContentUnit)
    if (this.containsIndexedTags(processedText)) {
      // Use styles from result, or fall back to namespace lookup
      const styles = resultStyles || this.getStylesFromNamespace(request);
      const unit: ContentUnit = {
        text: processedText,
        styles,
      };
      return this.htmlSerializer.serialize(unit);
    }

    // Check for HTML in defaultValue
    if (this.containsHtml(processedText)) {
      const unit = this.markupParser.parse(processedText);
      return this.htmlSerializer.serialize(unit);
    }

    // Plain text - return as-is
    return processedText;
  }

  /**
   * 📦 Process a value through ContentUnit or ICU pipeline
   *
   * Processing priority:
   * 1. Indexed tags (<1>...</1>) from server ContentUnit → convert to HTML at render time
   * 2. HTML tags (from defaultValue or legacy) → MarkupParser → ContentUnit → HTML
   * 3. Plain text → standard ICU processing
   *
   * @internal
   */
  private processValue(value: string, request: TranslationRequest): string {
    // Priority 1: Check for indexed tags (from server ContentUnit)
    // These need to be converted to HTML using the stored styles
    if (this.containsIndexedTags(value)) {
      return this.processIndexedValue(value, request);
    }

    // Priority 2: Auto-detect HTML and process through ContentUnit if found
    if (this.containsHtml(value)) {
      return this.processHtmlValue(value, request);
    }

    // Priority 3: Standard ICU processing for plain text
    return this.processTranslationValue(value, request);
  }

  /**
   * 📦 Check if a string contains indexed tags
   *
   * Used for auto-detection of server ContentUnit format.
   *
   * @param value - String to check
   * @returns true if indexed tags are detected
   */
  private containsIndexedTags(value: string): boolean {
    return INDEXED_TAG_REGEX.test(value);
  }

  /**
   * 📦 Process indexed value - returns PLAIN TEXT
   *
   * Strips indexed tags (<1>text</1>) → "text"
   * For HTML output, use tHtml() instead.
   *
   * @internal
   */
  private processIndexedValue(
    value: string,
    request: TranslationRequest
  ): string {
    // 1. Process ICU first (with indexed tags intact)
    const processedText = this.processTranslationValue(value, request);

    // 2. Strip indexed tags - t() returns plain text
    return processedText.replace(/<\/?[0-9]+\/?>/g, '');
  }

  /**
   * 📦 Get styles for a translation key from namespace cache (fallback)
   *
   * Used when styles are not available in the result (e.g., for fallback values).
   * Looks up the full ContentUnit from the namespace cache to get styles.
   *
   * @internal
   */
  private getStylesFromNamespace(
    request: TranslationRequest
  ): Record<string, string[]> {
    if (!request.namespace) {
      return {};
    }

    try {
      // Get namespace cache key: @locale:namespace
      const namespaceCacheKey = createNamespaceCacheKey(
        request.locale,
        request.namespace
      );

      // Get namespace data from handler chain
      const namespaceData = this.handlerChain.getNamespaceData(namespaceCacheKey);
      if (!namespaceData) {
        return {};
      }

      // Resolve the content unit for this key (server always returns ContentUnit format)
      const resolved = resolveNestedKey(namespaceData, request.cleanKey);

      if (resolved && typeof resolved === 'object' && 'styles' in resolved) {
        const contentUnit = resolved as ContentUnit;
        return contentUnit.styles;
      }

      return {};
    } catch (error) {
      logger.debug(`Error getting styles for key ${request.cleanKey}:`, error);
      return {};
    }
  }

  /**
   * 📦 Process HTML value - returns PLAIN TEXT
   *
   * Parses HTML to ContentUnit, processes ICU, strips indexed tags.
   * For HTML output, use tHtml() instead.
   *
   * @internal
   */
  private processHtmlValue(value: string, request: TranslationRequest): string {
    try {
      // 1. Parse HTML to ContentUnit (converts HTML tags to indexed format)
      const unit = this.markupParser.parse(value);

      // 2. Process ICU expressions in the text
      const processedText = this.processTranslationValue(unit.text, request);

      // 3. Strip indexed tags - t() returns plain text
      return processedText.replace(/<\/?[0-9]+\/?>/g, '');
    } catch (error) {
      // The key was in scope here and went unlogged, which left the line
      // naming the content but not the row. See utils/log-safe.ts for why the
      // value is described rather than reproduced.
      // The error goes in the MESSAGE, not the data argument: this logger
      // renders data with JSON.stringify, and an Error stringifies to `{}`, so
      // the reason has never reached the log from here.
      logger.warn(
        `ContentUnit processing failed for key "${request.cleanKey}" — ` +
          `${describeContent(value)}: ${describeError(error)}`
      );
      // Fallback to standard processing
      return this.processTranslationValue(value, request);
    }
  }

  /**
   * ✅ Process translation value through embedded plugins
   *
   * Public overload for Trans component usage (simple API)
   */
  public processTranslationValue(
    value: string,
    params?: Record<string, unknown>,
    locale?: string,
    key?: string
  ): string;

  /**
   * Internal overload (with full TranslationRequest for t() method)
   */
  public processTranslationValue(
    value: string,
    request: TranslationRequest
  ): string;

  /**
   * Implementation (handles both overloads)
   */
  public processTranslationValue(
    value: string,
    paramsOrRequest?: Record<string, unknown> | TranslationRequest,
    locale?: string,
    key?: string
  ): string {
    // Determine which overload was called
    let params: Record<string, unknown> | undefined;
    let resolvedLocale: string;
    let resolvedKey: string | undefined;

    if (paramsOrRequest && 'cleanKey' in paramsOrRequest) {
      // Internal overload: TranslationRequest
      const request = paramsOrRequest as TranslationRequest;
      params = request.parsedParams;
      resolvedLocale = request.locale;
      resolvedKey = request.cleanKey;
    } else {
      // Public overload: simple parameters
      params = paramsOrRequest as Record<string, unknown> | undefined;
      resolvedLocale = locale || this.currentLocale;
      resolvedKey = key;
    }

    let processedValue = value;

    try {
      // ✅ Step 1: Handle structured translation data (plurals, context variations)
      processedValue = this.applyStructuralFormatters(
        processedValue,
        params,
        resolvedKey
      );

      // ✅ Step 2: Apply interpolation through embedded formatter plugin
      // ICU messages need processing even without params (for 'other' defaults)
      if (this.formatters.has('interpolate')) {
        const interpolateFormatter = this.formatters.get('interpolate')!;
        processedValue = interpolateFormatter(
          processedValue,
          params,
          resolvedLocale,
          resolvedKey
        );
      } else if (
        !this.warnedNoInterpolate &&
        params &&
        Object.keys(params).length > 0 &&
        processedValue.includes('{')
      ) {
        // Params against a {placeholder} string with nothing to interpolate:
        // without this, the miswiring is invisible and the SDK looks broken.
        this.warnedNoInterpolate = true;
        // eslint-disable-next-line no-console
        console.warn(
          '[LionRapid] Translation params were passed but no interpolate ' +
            'formatter is registered — {placeholders} render literally. ' +
            'Install @lionrapid/formatters and add .use(coreFormattersPlugin) ' +
            'to the builder.'
        );
      }

      // ✅ Step 3: Apply value processors (escaping, sanitization, etc.)
      if (this.processors.size > 0) {
        for (const [name, processor] of this.processors.entries()) {
          try {
            processedValue = processor(processedValue, params, resolvedKey);
          } catch (error) {
            logger.warn(`Processor ${name} failed:`, error);
          }
        }
      }

      return processedValue;
    } catch (error) {
      logger.warn(
        `Translation processing failed for key "${resolvedKey ?? '(no key)'}" ` +
          `— ${describeContent(value)}: ${describeError(error)}`
      );
      return value;
    }
  }

  /**
   * 📦 CONTENT UNIT API
   * Returns translation as ContentUnit for JSX rendering (Trans component)
   *
   * This is the preferred method for components that need to render
   * styled content with JSX interpolation.
   *
   * @param key - Translation key
   * @param params - Optional interpolation parameters
   * @param defaultValue - Optional default value (can contain HTML)
   * @returns ContentUnit with indexed tags and styles
   *
   * @example
   * ```typescript
   * const unit = i18n.getContentUnit('terms', {}, 'Read our <b>Terms</b>.');
   * // → { text: "Read our <1>Terms</1>.", styles: {"1": ["raw:<b>"]} }
   * ```
   */
  getContentUnit(
    key: string,
    params?: Record<string, unknown>,
    defaultValue?: string
  ): ContentUnit {
    // Get translation value (ICU processing done separately per segment)
    const request = prepareTranslationRequest(
      key,
      this.currentLocale,
      params,
      defaultValue,
      this.defaultNamespace
    );

    // Try memory cache
    const syncResult = this.handlerChain.handleSync(request);
    const rawValue =
      syncResult && syncResult.status === 200
        ? syncResult.value
        : request.parsedDefaultValue || request.cleanKey;

    // If no translation found, trigger background load
    if (!syncResult || syncResult.status !== 200) {
      this.triggerBackgroundLoad(request);
    }

    // Parse HTML to ContentUnit
    return this.markupParser.parse(rawValue);
  }

  /**
   * 📦 Process ContentUnit text through ICU formatters
   *
   * Used by JSX serializers to process individual text segments.
   *
   * @param text - Text segment (may contain ICU expressions like {name})
   * @param params - Interpolation parameters
   * @returns Processed text with ICU expressions resolved
   */
  processContentText(text: string, params?: Record<string, unknown>): string {
    return this.processTranslationValue(text, params, this.currentLocale);
  }

  /**
   * 📦 Check if a string contains HTML tags
   *
   * Used for auto-detection of HTML in default values.
   *
   * @param value - String to check
   * @returns true if HTML tags are detected
   */
  containsHtml(value: string): boolean {
    return HTML_TAG_REGEX.test(value);
  }

  /**
   * ✅ Handle structured formatters (plural, context) - embedded in core
   */
  private applyStructuralFormatters(
    value: string,
    params?: Record<string, unknown>,
    translationKey?: string
  ): string {
    const translationData: string = value;

    // Type guard for params having a count property
    const hasCount = (
      p: Record<string, unknown> | undefined
    ): p is { count: number } => typeof p?.count === 'number';

    // Type guard for params having a context property
    const hasContext = (
      p: Record<string, unknown> | undefined
    ): p is { context: string } => typeof p?.context === 'string';

    // Apply plural formatter if available and applicable
    if (this.formatters.has('plural') && hasCount(params)) {
      const pluralFormatter = this.formatters.get('plural')!;
      try {
        const pluralResult = pluralFormatter(
          translationData,
          params,
          this.currentLocale,
          translationKey
        );
        if (pluralResult !== String(translationData)) {
          return pluralResult;
        }
      } catch (error) {
        logger.warn('Plural formatting failed:', error);
      }
    }

    // Apply context formatter if available and applicable
    if (this.formatters.has('context') && hasContext(params)) {
      const contextFormatter = this.formatters.get('context')!;
      try {
        const contextResult = contextFormatter(
          translationData,
          params,
          this.currentLocale,
          translationKey
        );
        if (contextResult !== String(translationData)) {
          return contextResult;
        }
      } catch (error) {
        logger.warn('Context formatting failed:', error);
      }
    }

    return String(translationData);
  }

  /**
   * Trigger background load via handler chain
   */
  private triggerBackgroundLoad(request: TranslationRequest): void {
    logger.debug(`Background load: ${request.cleanKey}`);
    const cacheKey = request.individualCacheKey;

    if (!this.pendingTranslations.has(cacheKey)) {
      const promise = this.handlerChain
        .handleAsync(request)
        .then(result => {
          if (result && result.value) {
            logger.debug(`Cached: ${request.cleanKey} = "${result.value}"`);
          } else {
            logger.debug(`Not found: ${request.cleanKey}`);
          }
        })
        .catch(error => {
          logger.warn(`Background load failed: ${request.cleanKey}`, error);
        });

      this.pendingTranslations.set(cacheKey, promise);
      promise.finally(() => this.pendingTranslations.delete(cacheKey));
    }
  }

  /**
   * Get the current locale
   */
  getCurrentLocale(): string {
    return this.currentLocale;
  }

  /**
   * Set default namespace (useful for scoped translations)
   */
  setDefaultNamespace(namespace?: string): void {
    this.defaultNamespace = namespace;
  }

  /**
   * Get current default namespace
   */
  getDefaultNamespace(): string | undefined {
    return this.defaultNamespace;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getEntries(): Record<string, any> {
    return this.handlerChain.getEntries();
  }

  isCached(key: string): boolean {
    const request = prepareTranslationRequest(
      key,
      this.currentLocale,
      undefined,
      undefined,
      this.defaultNamespace
    );

    return this.handlerChain.isCached(request);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getHandlerChainInfo(): any {
    return this.handlerChain.getChainInfo();
  }

  /**
   * ✅ Smart language change with selective cache clearing
   */
  async changeLanguage(
    newLocale: string,
    options?: {
      clear?: boolean;
      preloadNamespaces?: string[];
    }
  ): Promise<void> {
    const oldLocale = this.currentLocale;

    if (oldLocale === newLocale) {
      logger.debug(`Language already set to ${newLocale}`);
      return;
    }

    const clear = options?.clear || false;
    const languageBus = this.bus.language(oldLocale, newLocale);

    try {
      logger.info(`Language change: ${oldLocale} → ${newLocale}`);
      logger.debug('Language change options', {
        clear,
        preloadNamespaces: options?.preloadNamespaces,
      });

      languageBus.start();

      this.currentLocale = newLocale;

      if (clear) {
        await this.clearCache();
        this.clearOldLocalePendingTranslations(oldLocale);
      }

      if (options?.preloadNamespaces?.length) {
        await this.loadNamespaces(newLocale, options.preloadNamespaces);
      }

      languageBus.complete();

      logger.info(`Language changed: ${newLocale}`);
    } catch (error) {
      logger.error('Language change failed:', error);
      languageBus.error(error as Error);
      throw error;
    }
  }

  private async clearCache(): Promise<void> {
    this.handlerChain.clearCache();
    logger.debug(`Cache cleared for locale: ${this.currentLocale}`);
  }

  private clearOldLocalePendingTranslations(oldLocale: string): void {
    let clearedCount = 0;

    for (const key of this.pendingTranslations.keys()) {
      if (key.includes(`${oldLocale}:`)) {
        this.pendingTranslations.delete(key);
        clearedCount++;
      }
    }

    if (clearedCount > 0) {
      logger.debug(
        `Cleared ${clearedCount} pending translations for ${oldLocale}`
      );
    }
  }

  private async loadNamespaces(
    locale: string,
    namespaces: string[]
  ): Promise<void> {
    logger.debug(`Preloading ${namespaces.length} namespaces for ${locale}`);

    const preloadPromises = namespaces.map(async namespace => {
      try {
        await this.handlerChain.loadNamespace?.(locale, namespace);
        logger.debug(`Preloaded: ${locale}:${namespace}`);
      } catch (error) {
        logger.warn(`Failed to preload ${locale}:${namespace}:`, error);
      }
    });

    await Promise.allSettled(preloadPromises);
  }

  /**
   * Reload a namespace by clearing cache and fetching fresh data from server
   * Useful after syncing new translations to ensure fresh data
   *
   * @param namespace - The namespace to reload
   * @param locale - Optional locale (defaults to current locale)
   * @deprecated Use cache.invalidate({ namespace, refetch: true }) instead
   */
  async reloadNamespace(namespace: string, locale?: string): Promise<void> {
    const targetLocale = locale || this.currentLocale;
    logger.debug(`Reloading namespace: ${targetLocale}:${namespace}`);

    // Delegate to cache manager
    await this.cache.invalidate({
      namespace,
      locale: targetLocale,
      refetch: true,
    });

    logger.info(`Namespace reloaded: ${targetLocale}:${namespace}`);
  }

  // Helper methods
  private isEmbeddedPlugin(plugin: IPlugin): boolean {
    return plugin.capabilities.some(cap =>
      ['formatters', 'processors', 'pluralization', 'context'].includes(cap)
    );
  }

  private isEventPlugin(plugin: IPlugin): boolean {
    return plugin.capabilities.some(cap =>
      [
        'analytics',
        'logging',
        'react',
        'validation',
        'dev-tools',
        'monitoring',
      ].includes(cap)
    );
  }
}
